import React, { ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { AppColorVariant, colorVariantClasses } from "../../theme/AppColorVariant";

/**
 * Solid background classes for confirm buttons, keyed by AppColorVariant.
 * These provide filled button styling that maps to each status color.
 */
const confirmButtonClasses: Record<AppColorVariant, string> = {
  primary: "bg-primary text-primary-foreground hover:bg-primary/90",
  neutral:
    "bg-muted text-muted-foreground hover:bg-muted/80",
  danger:
    "bg-destructive text-white hover:bg-destructive/90",
  success:
    "bg-emerald-600 text-white hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600",
  warning:
    "bg-amber-500 text-white hover:bg-amber-600 dark:bg-amber-600 dark:hover:bg-amber-500",
  pink:
    "bg-pink-500 text-white hover:bg-pink-600 dark:bg-pink-600 dark:hover:bg-pink-500",
  lime:
    "bg-lime-500 text-white hover:bg-lime-600 dark:bg-lime-600 dark:hover:bg-lime-500",
  yellow:
    "bg-yellow-500 text-white hover:bg-yellow-600 dark:bg-yellow-600 dark:hover:bg-yellow-500",
  lavender:
    "bg-lavender-500 text-white hover:bg-lavender-600 dark:bg-lavender-600 dark:hover:bg-lavender-500",
};

interface ConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  message: string;
  statusColor: "primary" | "neutral" | "danger" | "success" | "warning" | "pink" | "lime" | "yellow";
  buttonText: string;
  maxWidth?: string | number;
  /** Keeps the confirm button disabled, for a dialog whose content must be filled in first. */
  confirmDisabled?: boolean;
  children?: ReactNode;
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  open,
  onClose,
  onConfirm,
  message,
  statusColor,
  buttonText,
  maxWidth,
  confirmDisabled,
  children,
}) => {
  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent
        role="alertdialog"
        showCloseButton={false}
        style={maxWidth ? { maxWidth } : undefined}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="size-5 text-amber-500" />
            Confirmation required
          </DialogTitle>
        </DialogHeader>

        <Separator />

        <p className="text-sm text-foreground">{message}</p>

        {children}

        <Separator />

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            className="min-w-[80px]"
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={confirmDisabled}
            className={cn("min-w-[80px]", confirmButtonClasses[statusColor])}
          >
            {buttonText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmationModal;
